import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import type { ScenarioCompletion } from '../../voice-agent/types/scenarios';
import { extractArrayFromResponse } from '../utils';

const SCENARIO_COMPLETIONS_QUERY_KEY = 'scenarioCompletions';

/**
 * Fetch scenario completions for the current user
 */
export function useMyCompletions({ enabled = true }: { enabled?: boolean } = {}) {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		queryFn: async () => {
			const response = await userSession.webServiceHelper.callWebServiceAsync<
				ScenarioCompletion[] | { value?: ScenarioCompletion[]; values?: ScenarioCompletion[] }
			>(Api.ImpersonationBroker.composeApiUrl({ urlPath: 'aida/scenario-completions' }), 'GET');
			return extractArrayFromResponse<ScenarioCompletion>(response.value);
		},
		queryKey: [SCENARIO_COMPLETIONS_QUERY_KEY, 'my-completions'],
	});
}

/**
 * Fetch scenario completions for a specific user (admin view)
 */
export function useUserCompletions({ userId, enabled = true }: { userId: string; enabled?: boolean }) {
	const userSession = useUserSession();
	return useQuery({
		enabled: enabled && !!userId,
		queryFn: async () => {
			const response = await userSession.webServiceHelper.callWebServiceAsync<
				ScenarioCompletion[] | { value?: ScenarioCompletion[]; values?: ScenarioCompletion[] }
			>(Api.ImpersonationBroker.composeApiUrl({ urlPath: `aida/scenario-completions/user/${userId}` }), 'GET');
			return extractArrayFromResponse<ScenarioCompletion>(response.value);
		},
		queryKey: [SCENARIO_COMPLETIONS_QUERY_KEY, 'user', userId],
	});
}
